import { useCallback, useEffect, useMemo, useState } from 'react';
import { RechargeReportErrorCode } from '../enums';
import { buildCpfRechargeReportFilename, buildOrderRechargeReportFilename } from '../utils/fileNamingUtils';
import { matchesBeneficiarySearch, RECHARGE_REPORT_TYPE_BENEFICIARIES, RECHARGE_REPORT_TYPE_ORDERS, } from '../utils/rechargeReportUtils';
import { useEmissorContractCollaborators } from './useEmissorContractCollaborators';
import { useEmissorRechargeReportPdf } from './useEmissorRechargeReportPdf';
const ALERT_BY_ERROR_CODE = {
    [RechargeReportErrorCode.NO_RECHARGES_FOUND]: 'empty',
    [RechargeReportErrorCode.NO_ORDERS_FOUND]: 'empty',
    [RechargeReportErrorCode.REPORT_ROW_LIMIT_EXCEEDED]: 'limit-exceeded',
    [RechargeReportErrorCode.CONTRACT_NOT_FOUND]: 'contract-not-found',
    [RechargeReportErrorCode.INVALID_INPUT]: 'invalid-input',
    [RechargeReportErrorCode.INVALID_REQUEST_BODY]: 'invalid-input',
};
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
function toIsoDate(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function isRealCalendarDate(value) {
    if (!ISO_DATE_PATTERN.test(value)) {
        return false;
    }
    const parsed = new Date(`${value}T00:00:00`);
    return !Number.isNaN(parsed.getTime()) && toIsoDate(parsed) === value;
}
export function useEmissorRechargeReportFlow(contractId, contractLegalName) {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState('modality');
    const [modality, setModality] = useState(null);
    const [periodPreset, setPeriodPreset] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [productIds, setProductIds] = useState([]);
    const [screenStatuses, setScreenStatuses] = useState([]);
    const [alert, setAlert] = useState(null);
    const { fetchReport, isLoading: isGenerating } = useEmissorRechargeReportPdf();
    const { beneficiaries, total, isLoading, isError } = useEmissorContractCollaborators({
        contractId: contractId ?? null,
        enabled: isOpen,
    });
    const resetState = useCallback(() => {
        setStep('modality');
        setModality(null);
        setPeriodPreset(null);
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setSelectedIds([]);
        setProductIds([]);
        setScreenStatuses([]);
        setAlert(null);
    }, []);
    useEffect(() => {
        setIsOpen(false);
        resetState();
    }, [contractId, resetState]);
    const open = useCallback(() => {
        resetState();
        setIsOpen(true);
    }, [resetState]);
    const cancel = useCallback(() => {
        setIsOpen(false);
        resetState();
    }, [resetState]);
    const visibleBeneficiaries = useMemo(() => beneficiaries.filter(beneficiary => matchesBeneficiarySearch(beneficiary, searchTerm)), [beneficiaries, searchTerm]);
    const dateError = useMemo(() => {
        if (!startDate && !endDate) {
            return null;
        }
        if ((startDate && !isRealCalendarDate(startDate)) || (endDate && !isRealCalendarDate(endDate))) {
            return 'invalid';
        }
        if (startDate && endDate && startDate > endDate) {
            return 'start-after-end';
        }
        return null;
    }, [endDate, startDate]);
    const hasValidPeriod = Boolean(startDate) && Boolean(endDate) && dateError === null;
    const isByOrder = modality === 'orders';
    const canExport = hasValidPeriod && (isByOrder || selectedIds.length > 0) && !isGenerating;
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const areAllVisibleSelected = visibleBeneficiaries.length > 0 && visibleBeneficiaries.every(item => selectedIdSet.has(item.id));
    const areSomeVisibleSelected = visibleBeneficiaries.some(item => selectedIdSet.has(item.id));
    const selectModality = useCallback((next) => {
        setModality(next);
    }, []);
    const selectPeriodPreset = useCallback((preset) => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(start.getDate() - preset);
        setPeriodPreset(preset);
        setStartDate(toIsoDate(start));
        setEndDate(toIsoDate(today));
    }, []);
    const changeDates = useCallback((nextStart, nextEnd) => {
        setPeriodPreset(null);
        setStartDate(nextStart);
        setEndDate(nextEnd);
    }, []);
    const changeSearchTerm = useCallback((term) => {
        setSearchTerm(term);
    }, []);
    const toggleBeneficiary = useCallback((beneficiaryId) => {
        setSelectedIds(current => current.includes(beneficiaryId) ? current.filter(id => id !== beneficiaryId) : [...current, beneficiaryId]);
    }, []);
    const toggleAllVisible = useCallback(() => {
        const visibleIds = visibleBeneficiaries.map(item => item.id);
        setSelectedIds(current => {
            const allSelected = visibleIds.length > 0 && visibleIds.every(id => current.includes(id));
            if (allSelected) {
                return current.filter(id => !visibleIds.includes(id));
            }
            return Array.from(new Set([...current, ...visibleIds]));
        });
    }, [visibleBeneficiaries]);
    const changeProductIds = useCallback((next) => {
        setProductIds(next);
    }, []);
    const changeScreenStatuses = useCallback((next) => {
        setScreenStatuses(next);
    }, []);
    const dismissAlert = useCallback(() => {
        setAlert(null);
    }, []);
    const exportReport = useCallback(async () => {
        if (!contractId || !canExport) {
            return;
        }
        setAlert(null);
        const legalName = contractLegalName ?? '';
        const outcome = isByOrder
            ? await fetchReport({
                contractId,
                type: RECHARGE_REPORT_TYPE_ORDERS,
                startDate,
                endDate,
                productId: productIds,
                screenStatus: screenStatuses,
            }, buildOrderRechargeReportFilename(legalName, startDate, endDate))
            : await fetchReport({ contractId, type: RECHARGE_REPORT_TYPE_BENEFICIARIES, startDate, endDate, beneficiaryIds: selectedIds }, buildCpfRechargeReportFilename(legalName, startDate, endDate));
        if (outcome.status === 'success') {
            setIsOpen(false);
            resetState();
            return;
        }
        setAlert((outcome.code && ALERT_BY_ERROR_CODE[outcome.code]) || 'error');
    }, [
        canExport,
        contractId,
        contractLegalName,
        endDate,
        fetchReport,
        isByOrder,
        productIds,
        resetState,
        screenStatuses,
        selectedIds,
        startDate,
    ]);
    return {
        isOpen,
        step,
        modality,
        periodPreset,
        startDate,
        endDate,
        dateError,
        searchTerm,
        visibleBeneficiaries,
        totalBeneficiaries: total,
        selectedIds,
        productIds,
        screenStatuses,
        isBeneficiariesLoading: isLoading,
        isBeneficiariesError: isError,
        isGenerating,
        alert,
        canGoToNextStep: modality !== null,
        canExport,
        areAllVisibleSelected,
        areSomeVisibleSelected,
        open,
        cancel,
        goToModalityStep: () => setStep('modality'),
        goToByCpfStep: () => setStep('by-cpf'),
        goToByOrderStep: () => setStep('by-order'),
        goToParamsStep: () => setStep(isByOrder ? 'by-order' : 'by-cpf'),
        selectModality,
        selectPeriodPreset,
        changeDates,
        changeSearchTerm,
        toggleBeneficiary,
        toggleAllVisible,
        changeProductIds,
        changeScreenStatuses,
        dismissAlert,
        exportReport,
    };
}
